/**
 * System Configuration Manager
 * Allows admin to update credentials directly from web interface
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Settings, Mail, CreditCard, Save, TestTube, Eye, EyeOff, 
  CheckCircle, XCircle, Loader2, Database, Globe, RefreshCw, Send
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SystemConfigManager = () => {
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  
  // SMTP Config
  const [smtpConfig, setSmtpConfig] = useState({
    smtp_host: '',
    smtp_port: 465,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_use_ssl: true
  });
  const [smtpSource, setSmtpSource] = useState('');
  
  // Stripe Config
  const [stripeConfig, setStripeConfig] = useState({
    publishable_key: '',
    secret_key: '',
    webhook_secret: ''
  });
  const [stripeSource, setStripeSource] = useState('');
  const [stripeMode, setStripeMode] = useState('');
  
  // PlugnPay Config
  const [plugnpayConfig, setPlugnpayConfig] = useState({
    username: '',
    password: '',
    api_url: 'https://pay1.plugnpay.com',
    merchant_name: 'EstateFlow'
  });
  const [plugnpaySource, setPlugnpaySource] = useState('');
  
  // App Config
  const [appConfig, setAppConfig] = useState({
    app_base_url: 'https://apcestateflow.com',
    admin_email: '',
    financing_admin_email: ''
  });
  const [appSource, setAppSource] = useState('');

  // SendGrid Config
  const [sendgridConfig, setSendgridConfig] = useState({
    api_key: '',
    from_email: 'noreply@apcestateflow.com',
    from_name: 'Estate Flow'
  });
  const [sendgridSource, setSendgridSource] = useState('');
  const [testingSendgrid, setTestingSendgrid] = useState(false);

  // Get auth headers - use access_token (same as AdminDashboard)
  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token');
    return { 
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  useEffect(() => {
    loadAllConfigs();
  }, []);

  const loadAllConfigs = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [smtpRes, stripeRes, plugnpayRes, appRes] = await Promise.all([
        axios.get(`${API}/admin/config/smtp`, { headers }).catch(() => null),
        axios.get(`${API}/admin/config/stripe`, { headers }).catch(() => null),
        axios.get(`${API}/admin/config/plugnpay`, { headers }).catch(() => null),
        axios.get(`${API}/admin/config/app`, { headers }).catch(() => null)
      ]);

      // Also load SendGrid
      try {
        const sendgridRes = await axios.get(`${API}/admin/config/sendgrid`, { headers });
        if (sendgridRes?.data) {
          setSendgridConfig({
            api_key: '',
            from_email: sendgridRes.data.config.from_email || 'noreply@apcestateflow.com',
            from_name: sendgridRes.data.config.from_name || 'Estate Flow'
          });
          setSendgridSource(sendgridRes.data.source);
        }
      } catch (e) { console.log('SendGrid config not loaded'); }

      if (smtpRes?.data) {
        const cfg = smtpRes.data.config;
        setSmtpConfig({
          smtp_host: cfg.smtp_host || '',
          smtp_port: cfg.smtp_port || 465,
          smtp_username: cfg.smtp_username || '',
          smtp_password: '', // Don't show actual password
          smtp_from_email: cfg.smtp_from_email || '',
          smtp_use_ssl: cfg.smtp_use_ssl !== false
        });
        setSmtpSource(smtpRes.data.source);
      }

      if (stripeRes?.data) {
        const cfg = stripeRes.data.config;
        setStripeConfig({
          publishable_key: '',
          secret_key: '',
          webhook_secret: ''
        });
        setStripeSource(stripeRes.data.source);
        setStripeMode(cfg.mode || '');
      }

      if (plugnpayRes?.data) {
        const cfg = plugnpayRes.data.config;
        setPlugnpayConfig({
          username: cfg.username || '',
          password: '',
          api_url: cfg.api_url || 'https://pay1.plugnpay.com',
          merchant_name: cfg.merchant_name || 'EstateFlow'
        });
        setPlugnpaySource(plugnpayRes.data.source);
      }

      if (appRes?.data) {
        const cfg = appRes.data.config;
        setAppConfig({
          app_base_url: cfg.app_base_url || 'https://apcestateflow.com',
          admin_email: cfg.admin_email || '',
          financing_admin_email: cfg.financing_admin_email || ''
        });
        setAppSource(appRes.data.source);
      }
    } catch (error) {
      console.error('Error loading configs:', error);
    }
    setLoading(false);
  };

  const saveSMTPConfig = async () => {
    if (!smtpConfig.smtp_host || !smtpConfig.smtp_username || !smtpConfig.smtp_password) {
      toast.error('Please fill in all required SMTP fields');
      return;
    }
    
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/config/smtp`, smtpConfig, { headers });
      toast.success('SMTP configuration saved!');
      setSmtpSource('database');
    } catch (error) {
      toast.error('Failed to save SMTP config: ' + (error.response?.data?.detail || error.message));
    }
    setLoading(false);
  };

  const testSMTP = async () => {
    setTesting(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/admin/config/test-smtp`, {}, { headers });
      if (res.data.success) {
        toast.success('SMTP connection successful!');
      } else {
        toast.error('SMTP test failed: ' + res.data.error);
      }
    } catch (error) {
      toast.error('SMTP test failed: ' + (error.response?.data?.detail || error.message));
    }
    setTesting(false);
  };

  const saveStripeConfig = async () => {
    if (!stripeConfig.publishable_key || !stripeConfig.secret_key) {
      toast.error('Please fill in Stripe publishable and secret keys');
      return;
    }
    
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/config/stripe`, stripeConfig, { headers });
      const mode = stripeConfig.secret_key.startsWith('sk_live') ? 'LIVE' : 'TEST';
      toast.success(`Stripe configuration saved (${mode} mode)!`);
      setStripeSource('database');
      setStripeMode(mode);
    } catch (error) {
      toast.error('Failed to save Stripe config: ' + (error.response?.data?.detail || error.message));
    }
    setLoading(false);
  };

  const savePlugnPayConfig = async () => {
    if (!plugnpayConfig.username || !plugnpayConfig.password) {
      toast.error('Please fill in PlugnPay username and password');
      return;
    }
    
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/config/plugnpay`, plugnpayConfig, { headers });
      toast.success('PlugnPay configuration saved!');
      setPlugnpaySource('database');
    } catch (error) {
      toast.error('Failed to save PlugnPay config: ' + (error.response?.data?.detail || error.message));
    }
    setLoading(false);
  };

  const saveAppConfig = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/config/app`, appConfig, { headers });
      toast.success('Application configuration saved!');
      setAppSource('database');
    } catch (error) {
      toast.error('Failed to save app config: ' + (error.response?.data?.detail || error.message));
    }
    setLoading(false);
  };

  const saveSendGridConfig = async () => {
    if (!sendgridConfig.api_key) {
      toast.error('Please enter SendGrid API key');
      return;
    }
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/config/sendgrid`, sendgridConfig, { headers });
      toast.success('SendGrid configuration saved! It will be used as backup when SMTP fails.');
      setSendgridSource('database');
    } catch (error) {
      toast.error('Failed to save SendGrid config: ' + (error.response?.data?.detail || error.message));
    }
    setLoading(false);
  };

  const testSendGrid = async () => {
    setTestingSendgrid(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/admin/config/test-sendgrid`, {}, { headers });
      if (res.data.success) {
        toast.success('SendGrid API key is valid!');
      } else {
        toast.error('SendGrid test failed: ' + res.data.error);
      }
    } catch (error) {
      toast.error('SendGrid test failed: ' + (error.response?.data?.detail || error.message));
    }
    setTestingSendgrid(false);
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const SourceBadge = ({ source }) => (
    <Badge variant={source === 'database' ? 'default' : 'secondary'} className="ml-2">
      {source === 'database' ? (
        <><Database className="h-3 w-3 mr-1" /> Database</>
      ) : (
        <><Globe className="h-3 w-3 mr-1" /> Environment</>
      )}
    </Badge>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center">
              <Settings className="h-5 w-5 mr-2" />
              System Configuration
            </CardTitle>
            <CardDescription className="mt-2">
              Manage credentials and settings. Database settings override environment variables.
            </CardDescription>
          </div>
          <Button variant="outline" onClick={loadAllConfigs} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="smtp" className="space-y-4">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="smtp">
              <Mail className="h-4 w-4 mr-2" />
              Email SMTP
            </TabsTrigger>
            <TabsTrigger value="sendgrid">
              <Send className="h-4 w-4 mr-2" />
              SendGrid (Primary)
            </TabsTrigger>
            <TabsTrigger value="stripe">
              <CreditCard className="h-4 w-4 mr-2" />
              Stripe
            </TabsTrigger>
            <TabsTrigger value="plugnpay">
              <CreditCard className="h-4 w-4 mr-2" />
              PlugnPay
            </TabsTrigger>
            <TabsTrigger value="app">
              <Globe className="h-4 w-4 mr-2" />
              Application
            </TabsTrigger>
          </TabsList>

          {/* SMTP Configuration */}
          <TabsContent value="smtp" className="space-y-4">
            <div className="flex items-center mb-4">
              <h3 className="text-lg font-semibold">Email SMTP Settings</h3>
              {smtpSource && <SourceBadge source={smtpSource} />}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>SMTP Host *</Label>
                <Input
                  value={smtpConfig.smtp_host}
                  onChange={(e) => setSmtpConfig({...smtpConfig, smtp_host: e.target.value})}
                  placeholder="mail.example.com"
                />
              </div>
              <div>
                <Label>SMTP Port</Label>
                <Input
                  type="number"
                  value={smtpConfig.smtp_port}
                  onChange={(e) => setSmtpConfig({...smtpConfig, smtp_port: parseInt(e.target.value)})}
                  placeholder="465"
                />
              </div>
              <div>
                <Label>Username *</Label>
                <Input
                  value={smtpConfig.smtp_username}
                  onChange={(e) => setSmtpConfig({...smtpConfig, smtp_username: e.target.value})}
                  placeholder="no-reply@example.com"
                />
              </div>
              <div>
                <Label>Password *</Label>
                <div className="relative">
                  <Input
                    type={showPasswords.smtp ? 'text' : 'password'}
                    value={smtpConfig.smtp_password}
                    onChange={(e) => setSmtpConfig({...smtpConfig, smtp_password: e.target.value})}
                    placeholder="Enter password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => togglePasswordVisibility('smtp')}
                  >
                    {showPasswords.smtp ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div>
                <Label>From Email</Label>
                <Input
                  value={smtpConfig.smtp_from_email}
                  onChange={(e) => setSmtpConfig({...smtpConfig, smtp_from_email: e.target.value})}
                  placeholder="no-reply@example.com"
                />
              </div>
              <div className="flex items-center space-x-2 pt-6">
                <input
                  type="checkbox"
                  id="smtp_ssl"
                  checked={smtpConfig.smtp_use_ssl}
                  onChange={(e) => setSmtpConfig({...smtpConfig, smtp_use_ssl: e.target.checked})}
                  className="h-4 w-4"
                />
                <Label htmlFor="smtp_ssl">Use SSL (Port 465)</Label>
              </div>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button onClick={saveSMTPConfig} disabled={loading}>
                <Save className="h-4 w-4 mr-2" />
                Save SMTP Settings
              </Button>
              <Button variant="outline" onClick={testSMTP} disabled={testing}>
                {testing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <TestTube className="h-4 w-4 mr-2" />
                )}
                Test Connection
              </Button>
            </div>

            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-sm mb-2">Quick Fill - Working Credentials:</h4>
              <Button 
                variant="secondary" 
                size="sm"
                onClick={() => setSmtpConfig({
                  smtp_host: 'mail.supremecluster.com',
                  smtp_port: 465,
                  smtp_username: 'no-reply@apcestateflow.com',
                  smtp_password: 'K0H5N2bps-$$%',
                  smtp_from_email: 'no-reply@apcestateflow.com',
                  smtp_use_ssl: true
                })}
              >
                Fill with Default Credentials
              </Button>
            </div>
          </TabsContent>

          {/* SendGrid Configuration (Primary) */}
          <TabsContent value="sendgrid" className="space-y-4">
            <div className="flex items-center mb-4">
              <h3 className="text-lg font-semibold">SendGrid (Primary Email Service)</h3>
              {sendgridSource && <SourceBadge source={sendgridSource} />}
            </div>
            
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
              <p className="text-emerald-800 text-sm">
                <strong>SendGrid is the PRIMARY email service</strong> for all emails including 2FA codes, trial warnings, and notifications. SMTP is used as a fallback if SendGrid is unavailable.
              </p>
            </div>
            
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label>API Key *</Label>
                <div className="relative">
                  <Input
                    type={showPasswords.sendgrid ? 'text' : 'password'}
                    value={sendgridConfig.api_key}
                    onChange={(e) => setSendgridConfig({...sendgridConfig, api_key: e.target.value})}
                    placeholder="SG.xxxxxxxxxxxxxxxxxxxxxxxx"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => togglePasswordVisibility('sendgrid')}
                  >
                    {showPasswords.sendgrid ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div>
                <Label>From Email</Label>
                <Input
                  value={sendgridConfig.from_email}
                  onChange={(e) => setSendgridConfig({...sendgridConfig, from_email: e.target.value})}
                  placeholder="noreply@apcestateflow.com"
                />
              </div>
              <div>
                <Label>From Name</Label>
                <Input
                  value={sendgridConfig.from_name}
                  onChange={(e) => setSendgridConfig({...sendgridConfig, from_name: e.target.value})}
                  placeholder="Estate Flow"
                />
              </div>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button onClick={saveSendGridConfig} disabled={loading}>
                <Save className="h-4 w-4 mr-2" />
                Save SendGrid Settings
              </Button>
              <Button variant="outline" onClick={testSendGrid} disabled={testingSendgrid}>
                {testingSendgrid ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <TestTube className="h-4 w-4 mr-2" />
                )}
                Test API Key
              </Button>
            </div>

            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-sm mb-2">How to get SendGrid API Key:</h4>
              <ol className="text-sm text-gray-600 list-decimal list-inside space-y-1">
                <li>Go to <a href="https://sendgrid.com" target="_blank" rel="noreferrer" className="text-blue-600 underline">sendgrid.com</a> and create account</li>
                <li>Navigate to Settings → API Keys</li>
                <li>Create new API key with "Full Access"</li>
                <li>Copy and paste the key here</li>
              </ol>
            </div>
          </TabsContent>

          {/* Stripe Configuration */}
          <TabsContent value="stripe" className="space-y-4">
            <div className="flex items-center mb-4">
              <h3 className="text-lg font-semibold">Stripe Payment Gateway</h3>
              {stripeSource && <SourceBadge source={stripeSource} />}
              {stripeMode && (
                <Badge className={`ml-2 ${stripeMode === 'LIVE' ? 'bg-green-600' : 'bg-yellow-600'}`}>
                  {stripeMode} Mode
                </Badge>
              )}
            </div>
            
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label>Publishable Key *</Label>
                <Input
                  value={stripeConfig.publishable_key}
                  onChange={(e) => setStripeConfig({...stripeConfig, publishable_key: e.target.value})}
                  placeholder="pk_live_... or pk_test_..."
                />
              </div>
              <div>
                <Label>Secret Key *</Label>
                <div className="relative">
                  <Input
                    type={showPasswords.stripe ? 'text' : 'password'}
                    value={stripeConfig.secret_key}
                    onChange={(e) => setStripeConfig({...stripeConfig, secret_key: e.target.value})}
                    placeholder="sk_live_... or sk_test_..."
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => togglePasswordVisibility('stripe')}
                  >
                    {showPasswords.stripe ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div>
                <Label>Webhook Secret (Optional)</Label>
                <Input
                  type="password"
                  value={stripeConfig.webhook_secret}
                  onChange={(e) => setStripeConfig({...stripeConfig, webhook_secret: e.target.value})}
                  placeholder="whsec_..."
                />
              </div>
            </div>
            
            <Button onClick={saveStripeConfig} disabled={loading} className="mt-4">
              <Save className="h-4 w-4 mr-2" />
              Save Stripe Settings
            </Button>

            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-sm mb-2">Quick Fill - LIVE Credentials:</h4>
              <Button 
                variant="secondary" 
                size="sm"
                onClick={() => setStripeConfig({
                  publishable_key: 'pk_live_UddgD2P5FoFWh0upnn6jJbji',
                  secret_key: 'sk_live_cskfJDtaLBAgkvArMqQRewmC',
                  webhook_secret: 'whsec_GCNOIVGCQlhFE2ItoBCFRZomWYyldFXT'
                })}
              >
                Fill with LIVE Credentials
              </Button>
            </div>
          </TabsContent>

          {/* PlugnPay Configuration */}
          <TabsContent value="plugnpay" className="space-y-4">
            <div className="flex items-center mb-4">
              <h3 className="text-lg font-semibold">PlugnPay Payment Gateway</h3>
              {plugnpaySource && <SourceBadge source={plugnpaySource} />}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Username *</Label>
                <Input
                  value={plugnpayConfig.username}
                  onChange={(e) => setPlugnpayConfig({...plugnpayConfig, username: e.target.value})}
                  placeholder="Username"
                />
              </div>
              <div>
                <Label>Password *</Label>
                <div className="relative">
                  <Input
                    type={showPasswords.plugnpay ? 'text' : 'password'}
                    value={plugnpayConfig.password}
                    onChange={(e) => setPlugnpayConfig({...plugnpayConfig, password: e.target.value})}
                    placeholder="Password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => togglePasswordVisibility('plugnpay')}
                  >
                    {showPasswords.plugnpay ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div>
                <Label>API URL</Label>
                <Input
                  value={plugnpayConfig.api_url}
                  onChange={(e) => setPlugnpayConfig({...plugnpayConfig, api_url: e.target.value})}
                  placeholder="https://pay1.plugnpay.com"
                />
              </div>
              <div>
                <Label>Merchant Name</Label>
                <Input
                  value={plugnpayConfig.merchant_name}
                  onChange={(e) => setPlugnpayConfig({...plugnpayConfig, merchant_name: e.target.value})}
                  placeholder="EstateFlow"
                />
              </div>
            </div>
            
            <Button onClick={savePlugnPayConfig} disabled={loading} className="mt-4">
              <Save className="h-4 w-4 mr-2" />
              Save PlugnPay Settings
            </Button>

            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-sm mb-2">Quick Fill - Credentials:</h4>
              <Button 
                variant="secondary" 
                size="sm"
                onClick={() => setPlugnpayConfig({
                  username: 'sebastiana2',
                  password: 'ZQZeJEfuXQmXKqGK',
                  api_url: 'https://pay1.plugnpay.com',
                  merchant_name: 'EstateFlow'
                })}
              >
                Fill with Default Credentials
              </Button>
            </div>
          </TabsContent>

          {/* Application Configuration */}
          <TabsContent value="app" className="space-y-4">
            <div className="flex items-center mb-4">
              <h3 className="text-lg font-semibold">Application Settings</h3>
              {appSource && <SourceBadge source={appSource} />}
            </div>
            
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label>Application Base URL</Label>
                <Input
                  value={appConfig.app_base_url}
                  onChange={(e) => setAppConfig({...appConfig, app_base_url: e.target.value})}
                  placeholder="https://apcestateflow.com"
                />
              </div>
              <div>
                <Label>Admin Email</Label>
                <Input
                  value={appConfig.admin_email}
                  onChange={(e) => setAppConfig({...appConfig, admin_email: e.target.value})}
                  placeholder="admin@example.com"
                />
              </div>
              <div>
                <Label>Financing Admin Email</Label>
                <Input
                  value={appConfig.financing_admin_email}
                  onChange={(e) => setAppConfig({...appConfig, financing_admin_email: e.target.value})}
                  placeholder="financing@example.com"
                />
              </div>
            </div>
            
            <Button onClick={saveAppConfig} disabled={loading} className="mt-4">
              <Save className="h-4 w-4 mr-2" />
              Save Application Settings
            </Button>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default SystemConfigManager;
